import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type MutableRefObject,
    type ReactNode,
} from 'react';

import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import type { ColumnDef, ExpandedState } from '@tanstack/react-table';

import { DataTable, type DataTableFiltersApi } from 'components/DataTable';

import { Card } from '@/components/ui/card';

import {
    UNASSIGNED_DROPPABLE_ID,
    approIdOf,
    type DynamicDeliveryFilter,
    type PlanningTableRow,
} from '../constants/dynamicDeliveryColumns';
import {
    GROUP_BY,
    LEGEND,
    POTENTIAL_DISPLAY_LIMIT,
    SECTION_PINNED_SORT,
    type TableVariant,
} from '../constants/planningView';
import { renderPlanningGroupRow } from './PlanningGroupRow';

type PlanningAnomaly = { totemId: string; siteName: string; columnCount: number; isActive: boolean };

// Min height of the sticky chrome (view switcher, + the action bar when a selection is active). The table
// header sticks right below it; its real height is measured at runtime so the offset follows when it grows.
const MIN_STICKY_TOOLBAR_HEIGHT = 52;

type PlanningTableViewProps = {
    topBanner?: ReactNode;
    stickyToolbar?: ReactNode;
    // 'appro' or 'site' — drives the grouping and the block-end row marking.
    view: TableVariant;
    columns: ColumnDef<PlanningTableRow, unknown>[];
    data: PlanningTableRow[];
    // A filter lifts the display cap, so the group headers must count from the filtered leaves, not the full set.
    hasActiveFilters: boolean;
    expanded: ExpandedState;
    onExpandedChange: (expanded: ExpandedState) => void;
    onFiltersChange: (filters: DynamicDeliveryFilter) => void;
    filtersApiRef: MutableRefObject<DataTableFiltersApi | null>;
    approColors: Map<string, string>;
    selectedKeys: string[];
    onSelectionChange: (keys: string[]) => void;
    isLoading: boolean;
    anomalies: PlanningAnomaly[];
    anomalyColumnCount: number;
    hiddenPotentialCount: number;
    potentialCount: number;
};

// The grouped planning table, shared by the « Par appro » and « Par site » views (only `view` → groupBy
// differs). Owns its scrollable banner (legend + display cap note + GPS anomalies) and the anomalies
// open/close state (purely local UI).
export const PlanningTableView = ({
    topBanner,
    stickyToolbar,
    view,
    columns,
    data,
    hasActiveFilters,
    expanded,
    onExpandedChange,
    onFiltersChange,
    filtersApiRef,
    approColors,
    selectedKeys,
    onSelectionChange,
    isLoading,
    anomalies,
    anomalyColumnCount,
    hiddenPotentialCount,
    potentialCount,
}: PlanningTableViewProps) => {
    // Collapsed by default: with hundreds of address-less columns the list would otherwise bury the table.
    const [anomaliesOpen, setAnomaliesOpen] = useState(false);

    // The sticky chrome (view switcher + the action bar when a selection is active) varies in height; measure it
    // so the table header pins right below it instead of overlapping when it grows or wraps.
    const stickyRef = useRef<HTMLDivElement>(null);
    const [stickyHeight, setStickyHeight] = useState(MIN_STICKY_TOOLBAR_HEIGHT);
    useEffect(() => {
        const node = stickyRef.current;
        if (!node) return undefined;
        const measure = () => setStickyHeight(node.offsetHeight);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Mark the last column of each appro (or site) block within a section so its card border can close at the bottom.
    const blockEndIds = useMemo(() => {
        const lastByBlock = new Map<string, string>();
        for (const row of data) {
            const key = `${row.isDeliver ? 'd' : 'p'}|${view === 'appro' ? approIdOf(row) : (row.site?._id ?? UNASSIGNED_DROPPABLE_ID)}`;
            lastByBlock.set(key, row._id);
        }
        return new Set(lastByBlock.values());
    }, [data, view]);
    const getRowClassName = useCallback(
        (row: PlanningTableRow) => (blockEndIds.has(row._id) ? 'planning-block planning-block-end' : 'planning-block'),
        [blockEndIds],
    );

    return (
        <TableContainer style={{ '--sticky-h': `${stickyHeight}px` } as CSSProperties}>
            <DataTable<PlanningTableRow, DynamicDeliveryFilter>
                columns={columns}
                data={data}
                filterMode="client"
                getRowId={(row) => row._id}
                searchPlaceholder="Site, colonne, code machine"
                disableUrlSync
                onFiltersChange={onFiltersChange}
                filtersApiRef={filtersApiRef}
                groupBy={GROUP_BY[view]}
                expanded={expanded}
                onExpandedChange={onExpandedChange}
                // With a filter active the cap is lifted and TanStack filters the leaves, so the
                // group headers must count from the (filtered) leafRows, not the full dataset.
                renderGroupRow={(params) =>
                    renderPlanningGroupRow({
                        ...params,
                        allRows: hasActiveFilters ? undefined : data,
                        approColors,
                    })
                }
                getRowClassName={getRowClassName}
                pinnedSort={SECTION_PINNED_SORT}
                enableSelection
                selectedKeys={selectedKeys}
                onSelectionChange={onSelectionChange}
                isLoading={isLoading}
                emptyMessage="Aucune colonne pour cette date."
                scrollableBanner={
                    // Fragment (not a single wrapper): the sticky switcher must be a DIRECT child of the scroll
                    // viewport to stay pinned across the whole list — nested in a short banner it would un-stick
                    // as soon as that banner scrolled off. Cards + legend/anomalies scroll away above it; table below.
                    <>
                        {topBanner}
                        <PlanningBanner>
                            <Legend aria-label="Légende des couleurs">
                                {LEGEND.map(({ token, label }) => (
                                    <span key={label}>
                                        <LegendDot $token={token} /> {label}
                                    </span>
                                ))}
                            </Legend>
                            {hiddenPotentialCount > 0 && (
                                <CapNote>
                                    Affichage limité aux {POTENTIAL_DISPLAY_LIMIT} premières colonnes potentielles (
                                    {hiddenPotentialCount} masquées sur {potentialCount}) pour garder le tableau fluide
                                    — la recherche et les filtres portent sur toutes les colonnes.
                                </CapNote>
                            )}
                            {anomalies.length > 0 && (
                                <AnomalyCard>
                                    <AnomalyHeader
                                        type="button"
                                        onClick={() => setAnomaliesOpen((open) => !open)}
                                        aria-expanded={anomaliesOpen}
                                    >
                                        {anomaliesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        <AlertTriangle size={18} />
                                        {anomalies.length} totem(s) · {anomalyColumnCount} colonne(s) sans coordonnées
                                        GPS — à corriger
                                    </AnomalyHeader>
                                    {anomaliesOpen && (
                                        <>
                                            <AnomalyText>
                                                Exclus du planning : leur totem n'a pas de coordonnées GPS. Renseignez
                                                l'adresse du totem pour les réafficher.
                                            </AnomalyText>
                                            <AnomalyList>
                                                {anomalies.map((anomaly) => (
                                                    <Link key={anomaly.totemId} to={`/totem/${anomaly.totemId}`}>
                                                        {anomaly.isActive ? '⚠ ' : ''}
                                                        {anomaly.siteName} ({anomaly.columnCount} colonne
                                                        {anomaly.columnCount > 1 ? 's' : ''})
                                                        {anomaly.isActive ? ' — à livrer !' : ''}
                                                    </Link>
                                                ))}
                                            </AnomalyList>
                                        </>
                                    )}
                                </AnomalyCard>
                            )}
                        </PlanningBanner>
                        {stickyToolbar && <StickyToolbar ref={stickyRef}>{stickyToolbar}</StickyToolbar>}
                    </>
                }
            />
        </TableContainer>
    );
};

const TableContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;

    /* The header sticks right below the pinned view switcher (not at the very top), so the two never overlap. */
    [data-slot='table'] thead th {
        top: var(--sticky-h, ${MIN_STICKY_TOOLBAR_HEIGHT}px);
    }

    /* Force separate borders so the per-appro card edges and rounded corners render. */
    [data-slot='table'] {
        border-collapse: separate;
        border-spacing: 0;
    }

    /* Row separators between columns (tr border-bottom doesn't paint in separate mode, so it's on the cells). */
    .planning-block > td {
        border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
    .planning-block-end > td {
        border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;

// Legend + cap note + anomalies. Sits below the sticky switcher in the scroll viewport and scrolls away with the
// rows. (Cards and the sticky switcher are siblings of this banner — see scrollableBanner above.)
const PlanningBanner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 12px;
`;

// View switcher pinned under the cards: stays reachable after the cards scroll off. Must be a direct child of the
// scroll viewport (not inside PlanningBanner) to stay pinned across the whole list. Opaque background so the
// cards/legend/rows scroll cleanly underneath; the table header pins right below it (height measured at runtime).
// Column layout so the action bar stacks under the switcher (same placement as the map's sticky toolbar).
const StickyToolbar = styled.div`
    position: sticky;
    top: 0;
    z-index: 30;
    min-height: ${MIN_STICKY_TOOLBAR_HEIGHT}px;
    margin: 12px 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    background: ${({ theme }) => theme.backgroundColor};
`;

const Legend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};

    > span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
`;

const CapNote = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const LegendDot = styled.span<{ $token: 'errorColor' | 'warningColor' | 'successColor' | 'infoTextColor' }>`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme, $token }) => theme[$token]};
`;

const AnomalyCard = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    border: 1px solid ${({ theme }) => theme.warningColor};
`;

const AnomalyHeader = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: ${({ theme }) => theme.warningColor};
    background: none;
    border: none;
    padding: 0;
    width: 100%;
    text-align: left;
    font-size: inherit;
    cursor: pointer;
`;

const AnomalyText = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const AnomalyList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    font-size: 13px;
    max-height: 160px;
    overflow-y: auto;

    a {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: ${({ theme }) => theme.ctaPrimaryColor};
        text-decoration: none;
    }
    a:hover {
        text-decoration: underline;
    }
    svg {
        color: ${({ theme }) => theme.warningColor};
        flex-shrink: 0;
    }
`;
